import { useSuspenseQuery } from "@tanstack/react-query";
import { Flex, Heading, Text, TextContainer } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import {
  getFundsRequestInfoQueryOptions,
  ReviewScreenSteps,
  useOffersPagePath,
  type FundingApplicationReviewDetailsElapsedTime,
} from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { SubsequentFundingFaq } from "../../../components/subsequent-funding-faq";
import { MESSAGES } from "../message-definitions";

export interface ReviewProps {
  reviewData: FundingApplicationReviewDetailsElapsedTime;
  airsData: FundingApplicationSchemas.AdditionalInformationRequestsData;
}

export const Review = ({ reviewData, airsData }: ReviewProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundsRequestInfo } = useSuspenseQuery(getFundsRequestInfoQueryOptions(customerDomain));
  const offersPagePath = useOffersPagePath(customerDomain, fundsRequestInfo.productRequestId);

  if (!fundsRequestInfo.productRequestId) {
    return <Navigate to="/" replace />;
  }

  if (fundsRequestInfo.currentStep === "offers_available") {
    return <Navigate to={offersPagePath} replace />;
  }

  return (
    <Flex direction="column" gap="16">
      <Flex direction="column" gap="10">
        <TextContainer>
          <Text>
            {reviewData.isSubsequentFunding ? (
              intl.formatMessage(MESSAGES.reviewScreenDescriptionSubsequentFunding)
            ) : (
              <FormattedMessage
                id="funding-application: review page for now description"
                defaultMessage="We are now reviewing your application. We will email you with updates or any requests for additional information. You can expect to hear back from us within <b>1-2 business days</b>."
                values={{
                  b: (chunks: any) => <strong>{chunks}</strong>,
                }}
              />
            )}
          </Text>
          {reviewData.isSubsequentFunding || airsData.hasActiveAirs || airsData.hasActiveManualAirs ? null : (
            <Text>{intl.formatMessage(MESSAGES.reviewScreenCloseWindowText)}</Text>
          )}
        </TextContainer>
        <Flex direction="column">
          <Heading size="xl">{intl.formatMessage(MESSAGES.reviewScreenYourApplicationStatusHeading)}</Heading>
          {reviewData.elapsedTime ? (
            <Text element="span">
              {intl.formatMessage(MESSAGES.reviewScreenApplicationSubmittedTimeSince, {
                elapsedTime: reviewData.elapsedTime,
              })}
            </Text>
          ) : null}
        </Flex>
        <Flex direction="column" align="flex-start" gap="6">
          <ReviewScreenSteps
            customerDomain={customerDomain}
            productRequestId={fundsRequestInfo.productRequestId}
            airsData={airsData}
            reviewData={reviewData}
          />
        </Flex>
      </Flex>
      {reviewData.isSubsequentFunding ? <SubsequentFundingFaq /> : null}
    </Flex>
  );
};
